import { useEffect, useState } from "react";
import { patchSubmissionMarks } from "@/api/staffColumns";
import { ApiError } from "@/api/client";
import {
  parseStaffNumberValue,
  parseStaffTextValue,
} from "@/lib/forms/staffColumns";
import type { StaffColumnDto, StaffMarkValue } from "@/types/staffColumn";

export function StaffMarkCell({
  formId,
  submissionId,
  column,
  value,
  disabled,
  onPatched,
  onError,
}: {
  formId: number;
  submissionId: number;
  column: StaffColumnDto;
  value: StaffMarkValue | undefined;
  disabled?: boolean;
  onPatched: (marks: Record<string, StaffMarkValue | null>) => void;
  onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState(
    value == null ? "" : String(value),
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  const save = async (next: StaffMarkValue | null) => {
    if (disabled || busy) return;
    const current = value ?? null;
    if (next === current) return;
    if (next == null && current == null) return;

    setBusy(true);
    onPatched({ [String(column.id)]: next });
    try {
      const result = await patchSubmissionMarks(formId, submissionId, {
        [String(column.id)]: next,
      });
      onPatched(result.marks);
    } catch (err) {
      onPatched({ [String(column.id)]: current });
      onError(
        err instanceof ApiError ? err.message : "Не удалось сохранить пометку",
      );
    } finally {
      setBusy(false);
    }
  };

  if (column.type === "checkbox") {
    return (
      <input
        type="checkbox"
        checked={value === true}
        disabled={disabled || busy}
        aria-label={column.name}
        onChange={(event) => void save(event.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500/30 disabled:opacity-50"
      />
    );
  }

  if (column.type === "status") {
    const choices = column.options?.choices ?? [];
    return (
      <select
        value={typeof value === "string" ? value : ""}
        disabled={disabled || busy}
        aria-label={column.name}
        onChange={(event) =>
          void save(event.target.value === "" ? null : event.target.value)
        }
        className="max-w-[10rem] rounded-md border border-slate-200 bg-white px-1.5 py-1 font-onest text-xs text-slate-800 disabled:bg-slate-50"
      >
        <option value="">—</option>
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    );
  }

  if (column.type === "number") {
    return (
      <input
        type="number"
        value={draft}
        disabled={disabled || busy}
        aria-label={column.name}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (!draft.trim()) {
            void save(null);
            return;
          }
          const parsed = parseStaffNumberValue(draft);
          if (parsed == null) {
            setDraft(value == null ? "" : String(value));
            return;
          }
          void save(parsed);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
          }
        }}
        className="w-24 rounded-md border border-slate-200 px-1.5 py-1 font-onest text-xs tabular-nums text-slate-800 disabled:bg-slate-50"
      />
    );
  }

  return (
    <input
      type="text"
      value={draft}
      disabled={disabled || busy}
      aria-label={column.name}
      maxLength={2000}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void save(parseStaffTextValue(draft))}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.currentTarget.blur();
        }
      }}
      className="min-w-[8rem] rounded-md border border-slate-200 px-1.5 py-1 font-onest text-xs text-slate-800 disabled:bg-slate-50"
    />
  );
}
